import { codeFromText, type PhoneSignInChannel, type SignInChannel } from "@teamna/shared";
import { useEffect, useId, useState } from "react";
import { isApiError } from "@teamna/web-client/api/client.ts";
import { copyForError } from "@teamna/web-client/api/copy.ts";

/**
 * The pieces of the one code flow (auth spec V2, section 7), shared by the sign-in screen and the
 * "Add a sign-in method" sheet so the two read the same.
 */

export const CODE_LENGTH = 6;
export const RESEND_AFTER_SECONDS = 30;

/**
 * Six boxes for the code. One real input sits over them, so paste, the phone's one-time-code
 * suggestion and the backspace key all work the way they do in any text field.
 *
 * The input is transparent, not `opacity: 0`: iOS Safari does not offer the "From Messages" code
 * for a field it treats as invisible. It has no `maxLength` either, because a pasted "123 456" or a
 * whole message would be cut before `codeFromText` could find the code in it.
 *
 * iOS suggests codes only from Messages and Mail, never from WhatsApp. WhatsApp's own "Copy code"
 * button puts the code on the clipboard, so the boxes carry a "Paste code" button, and say so for a
 * WhatsApp code.
 */
export function CodeBoxes({
  value,
  onChange,
  onComplete,
  invalid,
  channel,
}: {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  invalid?: boolean;
  /** The channel the code went on, for the WhatsApp hint. */
  channel?: SignInChannel;
}) {
  const id = useId();
  const [focused, setFocused] = useState(false);
  const [pasteProblem, setPasteProblem] = useState<string | null>(null);
  const digits = value.split("");
  const active = Math.min(value.length, CODE_LENGTH - 1);
  const canReadClipboard = typeof navigator !== "undefined" && typeof navigator.clipboard?.readText === "function";

  function take(text: string): void {
    const next = codeFromText(text);
    onChange(next);
    if (next.length === CODE_LENGTH) onComplete?.(next);
  }

  async function pasteFromClipboard(): Promise<void> {
    setPasteProblem(null);
    try {
      const text = await navigator.clipboard.readText();
      const found = codeFromText(text);
      if (found.length !== CODE_LENGTH) {
        setPasteProblem("There is no code to paste. Copy the code, then try again.");
        return;
      }
      take(found);
    } catch {
      // Refused by the browser or by the person. Long press on the boxes still pastes.
      setPasteProblem("Paste is not available here. Touch and hold the boxes, then tap Paste.");
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="eyebrow text-ink-2">
        Code
      </label>
      <div className="relative">
        <input
          id={id}
          value={value}
          onChange={(event) => {
            setPasteProblem(null);
            take(event.target.value);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-invalid={invalid ? true : undefined}
          className="absolute inset-0 z-10 h-full w-full cursor-text border-0 bg-transparent text-[16px] text-transparent caret-transparent outline-none selection:bg-transparent"
        />
        <div className="grid grid-cols-6 gap-2" aria-hidden="true">
          {Array.from({ length: CODE_LENGTH }, (_, index) => (
            <div
              key={index}
              className={[
                "sk-field flex h-14 items-center justify-center rounded-lg border text-[24px] font-semibold text-ink",
                invalid
                  ? "border-cancelled"
                  : focused && index === active
                    ? "border-accent"
                    : "border-line-strong",
              ].join(" ")}
            >
              {digits[index] ?? ""}
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 pt-1">
        <p className="text-[13px] text-ink-2">
          {channel === "whatsapp" ? "In WhatsApp, tap Copy code. Then tap Paste code." : null}
        </p>
        {canReadClipboard ? (
          <button
            type="button"
            onClick={() => void pasteFromClipboard()}
            className="min-h-10 shrink-0 rounded-lg px-2 text-[14px] font-semibold text-accent"
          >
            Paste code
          </button>
        ) : null}
      </div>
      {pasteProblem ? (
        <output className="text-[13px] text-ink-2">{pasteProblem}</output>
      ) : null}
    </div>
  );
}

/** Seconds left before "Resend" works again. `restart` is called after every send. */
export function useResendTimer(): { secondsLeft: number; restart: () => void } {
  const [until, setUntil] = useState(() => Date.now() + RESEND_AFTER_SECONDS * 1000);
  const [now, setNow] = useState(() => Date.now());
  const running = now < until;

  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);

  return {
    secondsLeft: Math.max(0, Math.ceil((until - now) / 1000)),
    restart: () => {
      const at = Date.now();
      setNow(at);
      setUntil(at + RESEND_AFTER_SECONDS * 1000);
    },
  };
}

/**
 * The other phone channel, for "Send by SMS instead" on the code screen. A number that is not on
 * WhatsApp takes the message without an error and never shows it, so the person needs a way out.
 * Null for an email code.
 */
export function otherPhoneChannel(channel: SignInChannel): PhoneSignInChannel | null {
  if (channel === "whatsapp") return "sms";
  if (channel === "sms") return "whatsapp";
  return null;
}

export function switchChannelLabel(channel: PhoneSignInChannel): string {
  return channel === "sms" ? "Send by SMS instead" : "Send by WhatsApp instead";
}

/** "by WhatsApp", "by SMS", "by email". */
export function channelWords(channel: SignInChannel): string {
  if (channel === "sms") return "by SMS";
  if (channel === "email") return "by email";
  return "on WhatsApp";
}

/**
 * The words for a failed call. The server's own detail is the most exact thing to show here ("That
 * code has expired. Ask for a new one."), so it comes first when there is one.
 */
export function messageForError(error: unknown): string {
  if (isApiError(error) && error.detail && error.status > 0 && error.code !== "RESPONSE_SHAPE") {
    return error.detail;
  }
  return copyForError(error).message;
}
